import { rem } from "@mantine/core";
import { useHover, useMergedRef } from "@mantine/hooks";
import { faTag, IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { useTheme } from "../../../../hooks";
import { Text } from "../../../text";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Group } from "../../../group";
import { Box } from "../../../box";
import { Stack } from "../../../stack";
import styles from "./chat.module.scss";
import { InteractionType, IPhraseTriggerResult, ITag, MessageDirection, Sentiment } from "@edgetier/watchtower-types";
import { Skeleton } from "../../../skeleton";
import { Markdown } from "../../../markdown";
import { formatClassNamesMessage, formatWritingIssueMessage } from "./chat.utilities";
import { useInteractionModalContext } from "../hooks";
import { faTagExternal } from "@awesome.me/kit-67bf90e9a4/icons/kit/custom";
import { Tag } from "./tag";
import { MessageOptions } from "./message-options";
import { useAudioContext } from "../../audio-context";
import { CurrentAudioMessagePosition } from "../context/context.types";

/**
 * Renders a full chat message bubble with author, content, tags, translation, writing issues, and contextual actions.
 * @param {string} props.messageId - The unique identifier of the message.
 * @param {MessageDirection} props.direction - The direction of the message (inbound or outbound).
 * @param {string} props.message - The raw message text content.
 * @param {object} props.sentiments - Object containing boolean flags for each sentiment type on the message.
 * @param {string} props.time - The formatted display time of the message.
 * @param {React.ReactNode} props.author - The rendered author element or skeleton.
 * @param {Array} props.tags - Optional array of tags attached to the message.
 * @param {Function} props.onSentimentToggle - Callback invoked when a sentiment is toggled, or undefined if disabled.
 * @param {React.ReactNode} props.addTagButton - The add-tag button element for this message.
 * @param {Date} props.sendDateTime - The original send date and time of the message.
 * @param {InteractionType} props.interactionType - The type of interaction this message belongs to.
 * @returns {JSX.Element} The Message component.
 */
const Message = ({
    messageId,
    direction,
    message,
    time,
    author,
    tags,
    sentiments,
    onSentimentToggle,
    addTagButton,
    sendDateTime,
    interactionType,
}: {
    direction: MessageDirection;
    messageId: string;
    message: string;
    sentiments: {
        hasFrustration?: boolean;
        hasConfusion?: boolean;
        hasGratitude?: boolean;
        hasPraise?: boolean;
        hasEmpathy?: boolean;
    };
    time: string;
    author?: React.ReactNode;
    tags?: (ITag & { isExternal: boolean })[];
    onSentimentToggle: ((sentiment: Sentiment) => Promise<void>) | undefined;
    addTagButton: React.ReactNode;
    sendDateTime: Date;
    interactionType: InteractionType;
}) => {
    const {
        interactionWritingIssues,
        actions,
        elements,
        interactionMessages: {
            highlightedMessageId,
            currentAudioMessageId,
            setHighlightedMessageId,
            isUserControllingScroll,
            setCurrentAudioMessagePosition,
        },
    } = useInteractionModalContext();
    const { isPlaying } = useAudioContext();
    const theme = useTheme();
    const { hovered, ref } = useHover();
    const messageRef = useRef<HTMLDivElement>(null);
    const mergedMessageRef = useMergedRef(ref, messageRef);
    const messageContentRef = useRef<HTMLDivElement>(null);

    const messageOptions = useMemo(
        () => (
            <MessageOptions
                {...sentiments}
                messageAudioSeek={elements?.audio?.messageAudioSeek}
                sendDateTime={sendDateTime}
                messageDirection={direction}
                interactionType={interactionType}
                addTagButton={addTagButton}
                onSentimentToggle={onSentimentToggle}
                visible={hovered}
            />
        ),
        [
            sentiments,
            elements?.audio?.messageAudioSeek,
            sendDateTime,
            direction,
            interactionType,
            addTagButton,
            onSentimentToggle,
            hovered,
        ]
    );

    // Scroll modal to message when message is highlighted and add focus styling
    // This is for auto scrolling to highlighted message from TagItem, AiAgentPerformanceSummaryContentDetails and Strong Emotion Widget
    useEffect(() => {
        if (messageRef.current && highlightedMessageId === messageId) {
            requestAnimationFrame(() => {
                messageRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                // Slight delay so the focus happens after the scroll
                const focusTimer = setTimeout(() => {
                    messageContentRef.current?.setAttribute("data-focused", "true");
                }, 100);

                const unfocusTimer = setTimeout(() => {
                    messageContentRef.current?.removeAttribute("data-focused");
                }, 2000);

                return () => {
                    clearTimeout(focusTimer);
                    clearTimeout(unfocusTimer);
                };
            });
        }
    }, [highlightedMessageId, messageId]);

    // Update setCurrentAudioMessagePosition if the currentAudioMessageId is in view or not
    useEffect(() => {
        if (currentAudioMessageId === null) {
            setCurrentAudioMessagePosition(null);
            return;
        }
        if (currentAudioMessageId !== messageId) return;

        let intersectionObserver: IntersectionObserver | undefined;

        const setupObserver = () => {
            if (!messageRef.current) return;
            const scrollViewport = messageRef.current.closest(".mantine-ScrollArea-viewport") as HTMLElement | null;
            if (!scrollViewport) return;

            intersectionObserver = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (!entry) return;

                    if (entry.isIntersecting) {
                        setCurrentAudioMessagePosition(null);
                        return;
                    }

                    const rect = entry.boundingClientRect;
                    const root = entry.rootBounds;
                    if (!root) return;

                    if (rect.bottom < root.top) {
                        setCurrentAudioMessagePosition(CurrentAudioMessagePosition.ABOVE);
                    } else if (rect.top > root.bottom) {
                        setCurrentAudioMessagePosition(CurrentAudioMessagePosition.BELOW);
                    } else {
                        setCurrentAudioMessagePosition(null);
                    }
                },
                {
                    root: scrollViewport,
                    rootMargin: "-20px 0px -20px 0px",
                    threshold: 0,
                }
            );
            intersectionObserver.observe(messageRef.current);
        };

        const rafId = requestAnimationFrame(setupObserver);

        return () => {
            cancelAnimationFrame(rafId);
            if (intersectionObserver !== undefined) {
                intersectionObserver.disconnect();
            }
        };
    }, [currentAudioMessageId, messageId, setCurrentAudioMessagePosition]);

    // Audio-driven highlighting and auto-scrolling to currentAudioMessageId
    useEffect(() => {
        let animationFrameId: number | undefined;
        let focusTimer: NodeJS.Timeout | undefined;

        if (messageRef.current && currentAudioMessageId === messageId && isPlaying) {
            animationFrameId = requestAnimationFrame(() => {
                if (!messageRef.current) return;

                const scrollViewport = messageRef.current.closest(".mantine-ScrollArea-viewport") as HTMLElement | null;
                if (!scrollViewport) return;

                const messageRect = messageRef.current.getBoundingClientRect();
                const viewportRect = scrollViewport.getBoundingClientRect();
                const VISIBILITY_BUFFER = 20;
                const isInView =
                    messageRect.top >= viewportRect.top + VISIBILITY_BUFFER &&
                    messageRect.bottom <= viewportRect.bottom - VISIBILITY_BUFFER;

                // If the user is not controlling scroll and the audio message is not in view, scroll to it
                if (!isUserControllingScroll && !isInView) {
                    messageRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }

                focusTimer = setTimeout(() => {
                    messageContentRef.current?.setAttribute("data-focused", "true");
                }, 100);
            });
        } else if (currentAudioMessageId !== null && currentAudioMessageId !== messageId) {
            messageContentRef.current?.removeAttribute("data-focused");
        }

        return () => {
            if (animationFrameId !== undefined) {
                cancelAnimationFrame(animationFrameId);
            }
            if (focusTimer) clearTimeout(focusTimer);
        };
    }, [
        currentAudioMessageId,
        messageId,
        isPlaying,
        isUserControllingScroll,
        highlightedMessageId,
        setCurrentAudioMessagePosition,
    ]);

    // Remove focus styling after 2 seconds when audio is not playing
    useEffect(() => {
        if (isPlaying) return;

        const unfocusTimer = setTimeout(() => {
            messageContentRef.current?.removeAttribute("data-focused");
        }, 2000);

        return () => {
            clearTimeout(unfocusTimer);
        };
    }, [highlightedMessageId, messageId, isPlaying]);

    // Remove highlight from message on click
    const unHighlightMessage = useCallback(() => {
        setHighlightedMessageId(null);
        messageContentRef.current?.removeAttribute("data-focused");
    }, [setHighlightedMessageId]);

    const isMessageLoading = useMemo(() => {
        const translation = actions?.translation?.activeTranslations[messageId] ?? null;
        const isTranslationLoading = translation?.isLoading;
        const isWritingIssuesLoading = actions?.writingIssues?.isEnabled && interactionWritingIssues.isLoading;
        return isTranslationLoading || isWritingIssuesLoading;
    }, [
        messageId,
        actions?.translation?.activeTranslations,
        actions?.writingIssues?.isEnabled,
        interactionWritingIssues.isLoading,
    ]);

    const [preparedMessage, setPreparedMessage] = useState<string>(message);

    useEffect(() => {
        setPreparedMessage(message);
    }, [message]);

    const toggleSentenceHighlight = useCallback(
        (isOpen: boolean, phraseTriggerTagDetails: IPhraseTriggerResult) => {
            const { sentence } = phraseTriggerTagDetails;
            if (isOpen) {
                // Highlight the phrase trigger sentence
                setPreparedMessage(
                    formatClassNamesMessage([sentence], message, {
                        clearPrevious: true,
                        className: styles["highlight"],
                    })
                );
            } else {
                // Remove the highlight from phrase trigger sentence
                setPreparedMessage(formatClassNamesMessage([], message, { clearPrevious: true }));
            }
        },
        [message]
    );

    const messageContent = useMemo(() => {
        let messageContent: string;
        const isTranslationEnabled = actions?.translation?.isEnabled ?? false;
        const translation = actions?.translation?.activeTranslations[messageId] ?? null;
        if (isTranslationEnabled && !translation?.isError) {
            if (!!translation?.translatedText) {
                messageContent = translation.translatedText;
            } else {
                messageContent = preparedMessage;
            }
        } else {
            messageContent = preparedMessage;
        }

        if (actions?.writingIssues?.isEnabled) {
            const messageWritingIssues = interactionWritingIssues.data
                ? (interactionWritingIssues.data?.[messageId] ?? [])
                : [];
            return formatWritingIssueMessage(messageWritingIssues, messageContent);
        }
        return messageContent;
    }, [
        actions?.translation?.isEnabled,
        actions?.writingIssues?.isEnabled,
        actions?.translation?.activeTranslations,
        interactionWritingIssues.data,
        preparedMessage,
        messageId,
    ]);

    return (
        <Stack
            className={styles["message"]}
            data-direction={direction.toLocaleLowerCase()}
            maw="75%"
            align={direction === MessageDirection.Outbound ? "flex-end" : "flex-start"}
            gap="xs"
            ref={mergedMessageRef}
            onClick={unHighlightMessage}
        >
            <Group gap="sm">{author}</Group>
            <Group wrap="nowrap" align="flex-start">
                {direction === MessageDirection.Outbound && messageOptions}
                <Box
                    p="md"
                    bg={
                        direction === MessageDirection.Outbound
                            ? "var(--surface-primary-solid-color)"
                            : "var(--surface-gray-fade)"
                    }
                    c={
                        direction === MessageDirection.Outbound
                            ? "var(--mantine-color-text-permanent-1)"
                            : "var(--mantine-color-neutral-9)"
                    }
                    maw={rem(540)}
                    style={
                        direction === MessageDirection.Outbound
                            ? {
                                  borderBottomRightRadius: theme.radius.lg,
                                  borderBottomLeftRadius: theme.radius.lg,
                                  borderTopRightRadius: 0,
                                  borderTopLeftRadius: theme.radius.lg,
                              }
                            : {
                                  borderBottomRightRadius: theme.radius.lg,
                                  borderBottomLeftRadius: theme.radius.lg,
                                  borderTopRightRadius: theme.radius.lg,
                                  borderTopLeftRadius: 0,
                              }
                    }
                    className={styles["message-content"]}
                    ref={messageContentRef}
                    tabIndex={-1}
                >
                    <Stack>
                        <Skeleton
                            color={
                                direction === MessageDirection.Outbound
                                    ? "var(--surface-primary-solid-color)"
                                    : undefined
                            }
                            visible={isMessageLoading ?? false}
                        >
                            <Markdown
                                markdownString={messageContent}
                                imageProps={{ maw: 100, preview: true }}
                                listProps={{ size: "sm" }}
                                textProps={{ "data-testid": "chat-message-content", size: "sm" }}
                            />
                        </Skeleton>
                        {tags && tags.length > 0 && (
                            <Group gap="xs">
                                {tags?.map((messageTag, tagIndex) => {
                                    const { tag, tagId, tagGroup, isExternal } = messageTag;
                                    return (
                                        <Tag
                                            key={`tag-${tagIndex}`}
                                            icon={isExternal ? (faTagExternal as IconDefinition) : faTag}
                                            text={tag}
                                            editTagButton={
                                                elements?.tags?.editTagButton && (
                                                    <elements.tags.editTagButton tagId={tagId} messageId={messageId} />
                                                )
                                            }
                                            removeTagButton={
                                                elements?.tags?.removeTagButton ? (
                                                    <elements.tags.removeTagButton
                                                        tag={messageTag}
                                                        messageId={messageId}
                                                    />
                                                ) : null
                                            }
                                            tagTooltipContent={
                                                elements?.tags?.tooltipContent && (
                                                    <elements.tags.tooltipContent
                                                        tag={tag}
                                                        tagId={tagId}
                                                        tagGroup={tagGroup}
                                                        messageId={messageId}
                                                        onMouseEnter={(details) => {
                                                            if (details) {
                                                                toggleSentenceHighlight(true, details);
                                                            }
                                                        }}
                                                        onMouseLeave={(details) => {
                                                            if (details) {
                                                                toggleSentenceHighlight(false, details);
                                                            }
                                                        }}
                                                    />
                                                )
                                            }
                                        />
                                    );
                                })}
                            </Group>
                        )}
                    </Stack>
                </Box>
                {direction === MessageDirection.Inbound && messageOptions}
            </Group>
            <Text variant="medium" size="xxs" dimmed>
                {time}
            </Text>
        </Stack>
    );
};

/**
 * Loading skeleton placeholder for the Message component.
 * @param {MessageDirection} props.direction - The direction determining skeleton alignment.
 * @returns {JSX.Element} The MessageSkeleton component.
 */
const MessageSkeleton = ({ direction }: { direction: MessageDirection }) => {
    const theme = useTheme();
    return (
        <Stack w="70%" align={direction === MessageDirection.Outbound ? "flex-end" : "flex-start"} gap="xs">
            <Text.Skeleton size="xs" width={150} />
            <Skeleton
                height={80}
                style={
                    direction === MessageDirection.Outbound
                        ? {
                              borderBottomRightRadius: theme.radius.lg,
                              borderBottomLeftRadius: theme.radius.lg,
                              borderTopRightRadius: 0,
                              borderTopLeftRadius: theme.radius.lg,
                          }
                        : {
                              borderBottomRightRadius: theme.radius.lg,
                              borderBottomLeftRadius: theme.radius.lg,
                              borderTopRightRadius: theme.radius.lg,
                              borderTopLeftRadius: 0,
                          }
                }
            />
            <Text.Skeleton size="xxs" width={100} />
        </Stack>
    );
};

Message.Skeleton = MessageSkeleton;

export { Message };
